import React, { useEffect, useState } from 'react';
import { Layout } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useLocation } from 'react-router-dom';
import clsx from 'clsx';
import auth from 'src/services/auth';
import { useAppStateContext } from 'src/stateProviders/useAppStateContext';
import SKIN_NAME from 'src/enums/skinName';
import CompanyLogo from './CompanyLogo';
import PrimaryMenu from './PrimaryMenu';
import HotLinksMenu from './HotLinksMenu';
import styles from './Sider.module.less';

const { Sider } = Layout;
const { Standard, DarkMode } = SKIN_NAME;

type SiderProps = {
  collapsed: boolean;
  onToggleCollapse: () => void;
  hasIqHubPrivilege: boolean;  // Add the prop here
};

const getFavicon = (skinName: SKIN_NAME): string => {
  switch (skinName) {
    case Standard:
    case DarkMode:
      return '/favicon.ico';
    default:
      return `/${skinName}.ico`;
  }
};

const MySider: React.FC<SiderProps> = ({ collapsed, onToggleCollapse, hasIqHubPrivilege }) => {
  const [isSiderHovered, setSiderHovered] = useState(false);
  const skinName = auth.getSkin() ?? SKIN_NAME.Standard;
  const location = useLocation();
  const pathnamesearch = location.pathname.concat(location.search);
  const { user = null } = useAppStateContext();

  const toggleCollapsed = () => {
    onToggleCollapse();
    localStorage.setItem('sliderState', JSON.stringify(!collapsed));
  };

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', skinName);
    const favicon = document.getElementById('favicon');
    if (favicon && 'href' in favicon) {
      favicon.href = getFavicon(skinName);
    }
  }, [auth.getSkin()]);

  return (
    <Sider
      theme="light"
      collapsed={collapsed}
      className={clsx(styles.sider, 'widget_color', { [styles.siderCollapsed]: collapsed })}
    >
      <div
        className={clsx(styles.logoContainer, 'siderLogo', {
          [styles.logoContainerCollapsed]: collapsed,
          siderLogoCollapsed: collapsed,
        })}
      >
        {localStorage.getItem('_SKIN') && <CompanyLogo skinName={skinName} collapsed={collapsed} />}
      </div>
      <span className={clsx(styles.triggerContainer, 'widget_color')} onClick={toggleCollapsed}>
        <FontAwesomeIcon icon={['far', collapsed ? 'chevron-right' : 'chevron-left']} size={'lg'} />
      </span>
      <div
        className={styles.navigationContainer}
        onMouseEnter={() => {
          if (collapsed && !isSiderHovered) {
            onToggleCollapse();
            setSiderHovered(true);
          }
        }}
        onMouseLeave={() => {
          if (isSiderHovered) {
            onToggleCollapse();
            setSiderHovered(false);
          }
        }}
      >
        <PrimaryMenu
          className={clsx(styles.menu, styles.topMenu)}
          pathName={pathnamesearch}
          hasIqHubPrivilege={hasIqHubPrivilege}
        />
        <HotLinksMenu className={styles.menu} pathName={pathnamesearch} user={user} collapsed={collapsed} />
      </div>
    </Sider>
  );
};

export default MySider;
